import React, { useMemo, useRef, useState } from 'react';
import { MapPin, ChevronDown, Search } from 'lucide-react';

const fmt = (n) => `€${Number(n || 0).toFixed(2)}`;

/**
 * Menu a tendina con ricerca per scegliere una sede / un punto di ritiro o
 * riconsegna. Si digita per filtrare per nome o indirizzo; frecce + Invio +
 * Escape funzionano da tastiera.
 *
 * Props:
 *  - id, label
 *  - points: [{ id, nome, indirizzo, supplementoRitiro, supplementoRiconsegna, isSede }]
 *  - value: id selezionato
 *  - onChange(id)
 *  - supplementKey: 'supplementoRitiro' | 'supplementoRiconsegna' | 'supplementoTotale' (quale mostrare accanto al nome;
 *    'supplementoTotale' va calcolato da chi chiama, es. ritiro+riconsegna quando sono nello stesso punto)
 */
const PuntoCombobox = ({ id, label, points, value, onChange, supplementKey = 'supplementoRitiro' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const selected = points.find((p) => p.id === value) || points[0];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return points;
    return points.filter((p) => `${p.nome} ${p.indirizzo || ''}`.toLowerCase().includes(q));
  }, [points, query]);

  const close = () => { setOpen(false); setQuery(''); setActive(0); };
  const choose = (p) => { onChange(p.id); close(); inputRef.current?.blur(); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && open && filtered[active]) { e.preventDefault(); choose(filtered[active]); }
    else if (e.key === 'Escape') { close(); }
  };

  const listId = `${id}-list`;
  const supplementLabel = (p) => {
    const s = p[supplementKey];
    return s > 0 ? `+${fmt(s)}` : (p.isSede ? 'incluso' : 'gratis');
  };

  return (
    <div className="relative">
      <label htmlFor={id} className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-2">
        {label}
      </label>
      <div className="relative">
        <MapPin className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          value={open ? query : selected.nome}
          placeholder={open ? 'Cerca una sede o un punto…' : ''}
          onFocus={() => { setOpen(true); setActive(Math.max(0, points.findIndex((p) => p.id === selected.id))); }}
          onBlur={close}
          onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true); }}
          onKeyDown={onKeyDown}
          className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-xl text-slate-700 text-sm focus:outline-none focus:border-[var(--brand,#2563EB)] focus:ring-1 focus:ring-[var(--brand,#2563EB)] transition bg-white box-border"
        />
        {open
          ? <Search className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          : <ChevronDown className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />}
      </div>

      {!open && selected.indirizzo && (
        <p className="text-xs text-slate-500 mt-1.5 truncate">{selected.indirizzo}</p>
      )}

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-auto bg-white border border-slate-200 rounded-xl shadow-lg py-1"
        >
          {filtered.length === 0 && (
            <li className="px-4 py-3 text-sm text-slate-500">Nessun punto trovato</li>
          )}
          {filtered.map((p, i) => (
            <li
              key={p.id}
              role="option"
              aria-selected={p.id === selected.id}
              // mouseDown (non click): scatta prima del blur dell'input, che chiuderebbe la lista
              onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              onMouseEnter={() => setActive(i)}
              className={`px-4 py-2.5 cursor-pointer flex items-start justify-between gap-3 ${i === active ? 'bg-slate-100' : ''}`}
            >
              <span className="min-w-0">
                <span className={`block text-sm ${p.id === selected.id ? 'font-bold text-slate-900' : 'text-slate-700'}`}>{p.nome}</span>
                {p.indirizzo && <span className="block text-xs text-slate-500 truncate">{p.indirizzo}</span>}
              </span>
              <span className="text-xs font-semibold whitespace-nowrap mt-0.5" style={{ color: p[supplementKey] > 0 ? '#2563EB' : '#059669' }}>
                {supplementLabel(p)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PuntoCombobox;
